/**
 * Tenant-side inbox for landlord announcements. Lives at
 * /tenant/announcements + a bell-count tile on the tenant dashboard.
 */
import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/lib/auth";
import { PageHeader, Card, Loader, EmptyState, Pill } from "@/components/ui/Primitives";
import { Megaphone, MailOpen, AlertTriangle } from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

export default function TenantAnnouncements() {
  const { user } = useAuth();
  const uid = user?.id || user?.user_id || user?.email || "";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    if (!uid) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/tenant/announcements`, { headers: { Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "x-user-id": uid }});
      const d = await r.json();
      setItems(d.items || []);
    } finally { setLoading(false); }
  }, [uid]);
  useEffect(() => { load(); }, [load]);

  async function markRead(nid) {
    if (openId === nid) { setOpenId(null); return; }
    setOpenId(nid);
    const found = items.find(it => it.id === nid);
    if (found && !found.read_at) {
      try {
        await fetch(`${API}/api/tenant/announcements/${nid}/read`, { method: "POST", headers: { Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "x-user-id": uid }});
        setItems(prev => prev.map(it => it.id === nid ? {...it, read_at: new Date().toISOString()} : it));
      } catch { /* swallow */ }
    }
  }

  if (loading) return <Loader/>;

  const unread = items.filter(it => !it.read_at).length;

  return (
    <div>
      <PageHeader
        title="Announcements from your landlord"
        subtitle={unread > 0 ? `You have ${unread} unread announcement${unread === 1 ? "" : "s"}.` : "Building updates, maintenance notices, and important reminders land here."}
      />
      {items.length === 0 ? (
        <EmptyState icon={Megaphone} title="No announcements yet" description="When your landlord sends an announcement, you'll find it here AND in your email."/>
      ) : (
        <div className="space-y-2">
          {items.map(it => {
            const a = it.announcement || {};
            const isOpen = openId === it.id;
            const isUnread = !it.read_at;
            return (
              <Card key={it.id} className={`p-4 cursor-pointer transition ${isUnread ? "border-emerald-300 bg-emerald-50/30" : "hover:border-slate-300"}`} onClick={() => markRead(it.id)} data-testid={`tenant-announcement-${it.id}`}>
                <div className="flex items-start gap-3">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${isUnread ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                    {isUnread ? <Megaphone className="w-4 h-4"/> : <MailOpen className="w-4 h-4"/>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-sm ${isUnread ? "font-extrabold text-slate-900" : "font-bold text-slate-700"} truncate`}>{a.subject || "(no subject)"}</span>
                      {a.importance === "urgent" && <Pill tone="red"><AlertTriangle className="w-2.5 h-2.5 inline mr-0.5"/> Urgent</Pill>}
                      {a.importance === "important" && <Pill tone="amber">Important</Pill>}
                      {isUnread && <span className="text-[10px] uppercase tracking-wide font-bold text-emerald-700">NEW</span>}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {a.landlord_name || "Your landlord"} · {a.created_at ? new Date(a.created_at).toLocaleString("en-CA", {dateStyle: "medium", timeStyle: "short"}) : ""}
                    </div>
                    {isOpen && (
                      <div className="text-sm text-slate-700 whitespace-pre-wrap mt-3 pt-3 border-t border-slate-100" data-testid={`tenant-announcement-body-${it.id}`}>
                        {a.body || "(no message)"}
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
